// client/src/components/influencer/InfluencerBankCheck.js
// 통장 등록 · 변경 — A안 리디자인: 민트 계좌 카드 + 나란히 변경 폼 (캐시·조회·저장·전체표시 로직은 기존 그대로)
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const BANKS = [
  '농협','광주','경남','국민','기업','아이엠 (구)대구','부산','산업','수협','신한','케이뱅크',
  '우체국','전북','SC제일','제주','우리','하나','한국씨티','홍콩(HSBC은행)','새마을금고','신협',
  '도이치','저축은행','뱅크오브아메리카','BNP파리바','JP모간','카카오뱅크','토스뱅크','기타(직접입력)'
];

function maskAccount(no) {
  if (!no) return '';
  const digits = String(no).replace(/\D/g, '');
  if (digits.length <= 6) return digits.replace(/./g, '*');
  const head = digits.slice(0, 6);
  const tail = digits.slice(6).replace(/./g, '*');
  return head + tail;
}

// 캐시 키
const keysOf = (uk) => ({
  exists: `influencer_bank_exists:${uk}`,
  holder: `influencer_bank_holder:${uk}`,
  bank: `influencer_bank_name:${uk}`,
  masked: `influencer_bank_masked:${uk}`,
});

// 동기 캐시 읽기 → 초기 렌더 즉시 반영
function readCached(userKey) {
  if (!userKey) return { hasBank: false, bank: null };
  const k = keysOf(userKey);
  const has = localStorage.getItem(k.exists) === '1';
  if (!has) return { hasBank: false, bank: null };
  const holderName = localStorage.getItem(k.holder) || '';
  const bankName = localStorage.getItem(k.bank) || '';
  const masked = localStorage.getItem(k.masked) || '';
  return { hasBank: true, bank: { holderName, bankName, accountNumberMasked: masked } };
}

function InfluencerBankCheck() {
  // 유저키 선캐시 로드
  const initialUserKey = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
  const cached = readCached(initialUserKey);

  const [sending, setSending] = useState(false);
  const [hasBank, setHasBank] = useState(cached.hasBank);
  const [bank, setBank] = useState(
    cached.bank
      ? { holderName: cached.bank.holderName, bankName: cached.bank.bankName, accountNumber: '' }
      : null
  );

  // 폼
  const [holderName, setHolderName] = useState(cached.bank?.holderName || '');
  const [bankName, setBankName] = useState('');
  const [bankNameCustom, setBankNameCustom] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  // 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const openModal = (msg) => { setModalMsg(msg); setModalOpen(true); };

  // 사용자 확정
  const [userKey, setUserKey] = useState(initialUserKey);
  const [userReady, setUserReady] = useState(!!initialUserKey);

  // 전부 표시 토글 상태
  const [showFull, setShowFull] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);

  // 서버 확정 + 상태 동기화
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const me = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
        const mj = await me.json().catch(() => ({}));
        if (!ignore && me.ok && mj?.ok) {
          const uk = (mj?.user?.userId || '').toLowerCase();
          if (uk) {
            setUserKey(uk);
            sessionStorage.setItem('current_user_key', uk);
            // 은행 상태 조회
            const res = await fetch(`${API_URL}/api/influencer/mybiz/bank`, { credentials: 'include' });
            const data = await res.json().catch(() => ({}));
            const k = keysOf(uk);
            if (res.ok && data?.ok && data?.exists && data?.bank) {
              const holder = data.bank.holderName || '';
              const bname = data.bank.bankName || '';
              const masked = maskAccount(data.bank.accountNumber || '');
              setHasBank(true);
              setBank({ holderName: holder, bankName: bname, accountNumber: data.bank.accountNumber || '' });
              setHolderName((prev) => prev || holder);
              // 캐시 갱신
              localStorage.setItem(k.exists, '1');
              localStorage.setItem(k.holder, holder);
              localStorage.setItem(k.bank, bname);
              localStorage.setItem(k.masked, masked);
            } else {
              setHasBank(false);
              setBank(null);
              // 캐시 클리어
              localStorage.removeItem(k.exists);
              localStorage.removeItem(k.holder);
              localStorage.removeItem(k.bank);
              localStorage.removeItem(k.masked);
            }
          }
        }
      } finally {
        if (!ignore) setUserReady(true);
      }
    })();
    return () => { ignore = true; };
  }, []);

  // 저장 처리
  const onSubmit = async () => {
    if (sending) return;
    if (!userReady || !userKey) { openModal('세션 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.'); return; }

    if (!holderName.trim() || !accountNumber.trim()) {
      openModal('예금주명과 계좌번호는 필수입니다.');
      return;
    }
    const finalBankName = bankName === '기타(직접입력)' ? (bankNameCustom || '').trim() : bankName;
    if (!finalBankName) { openModal('은행을 선택하거나 직접 입력해주세요.'); return; }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/bank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          holderName: holderName.trim(),
          bankName: finalBankName,
          bankNameCustom: bankName === '기타(직접입력)' ? finalBankName : '',
          accountNumber: accountNumber.trim()
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        const masked = maskAccount(accountNumber.trim());
        setHasBank(true);
        setBank({ holderName: holderName.trim(), bankName: finalBankName, accountNumber: accountNumber.trim() });
        setShowFull(false);
        setAccountNumber('');

        // 캐시 즉시 갱신 → 다음 진입에서도 깜빡임 없음
        const k = keysOf(userKey);
        localStorage.setItem(k.exists, '1');
        localStorage.setItem(k.holder, holderName.trim());
        localStorage.setItem(k.bank, finalBankName);
        localStorage.setItem(k.masked, masked);

        openModal('통장 정보가 등록되었습니다. 예금주명은 반드시 계약자명과 일치해야 포인트 환전이 가능합니다.');
      } else {
        openModal(data?.message || '등록/변경에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } catch {
      openModal('등록/변경에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    setSending(false);
  };

  const handleToggleReveal = async () => {
    if (showFull) { setShowFull(false); return; }
    if (bank?.accountNumber) { setShowFull(true); return; }
    // 캐시 초기 렌더 등으로 전체번호가 없는 경우 서버에서 즉시 보강
    setRevealLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/bank`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok && data?.exists && data?.bank?.accountNumber) {
        setBank(prev => ({
          ...(prev || {}),
          holderName: data.bank.holderName || prev?.holderName || '',
          bankName: data.bank.bankName || prev?.bankName || '',
          accountNumber: data.bank.accountNumber || ''
        }));
        setShowFull(true);
      }
    } finally {
      setRevealLoading(false);
    }
  };

  // 표시용 계좌번호 (캐시 기반 초기 진입에서도 마스킹 노출)
  const k = keysOf(userKey);
  const maskedFromCache = (userKey && localStorage.getItem(k.masked)) || '';
  const masked = bank?.accountNumber ? maskAccount(bank.accountNumber) : maskedFromCache;
  const displayNumber = showFull ? (bank?.accountNumber || '') : masked;
  const isEtc = bankName === '기타(직접입력)';

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">통장 등록 · 변경</h2>
        <p className="mb-page-description">환전 금액이 입금될 본인 명의 계좌를 등록해요.</p>
      </div>

      <div className="mb-grid-2" style={{ alignItems: 'start' }}>
        {/* 왼쪽: 등록된 계좌 카드 */}
        <div className="mb-card" style={{ margin: 0 }}>
          <h3 className="mb-section-title">등록된 계좌</h3>
          {hasBank ? (
            <>
              <div
                style={{
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #0fb37a, #0a8f62 70%, #065f42)',
                  color: '#fff',
                  padding: 20,
                  boxShadow: '0 14px 30px -16px rgba(15, 179, 122, 0.8)',
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.88 }}>
                  {bank?.bankName || (userKey && localStorage.getItem(k.bank)) || '-'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.01em', marginTop: 4, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>
                  {displayNumber || '-'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5, opacity: 0.88 }}>
                  예금주 {bank?.holderName || (userKey && localStorage.getItem(k.holder)) || '-'}
                  <button
                    type="button"
                    onClick={handleToggleReveal}
                    disabled={revealLoading}
                    style={{
                      marginLeft: 'auto', fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.16)',
                      borderRadius: 8, padding: '4px 10px', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35)',
                      color: '#fff', border: 0, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {showFull ? '가리기' : (revealLoading ? '로딩 중…' : '전체 번호 보기')}
                  </button>
                </div>
              </div>
              <div className="note note--mint">이 계좌로 환전 금액이 입금돼요. 예금주는 프리랜서 계약자명과 일치해야 해요.</div>
            </>
          ) : (
            <>
              <div className="mbh-todo__empty">아직 등록된 계좌가 없어요. 오른쪽에서 등록해주세요.</div>
              <div className="note note--amber">포인트를 환전하려면 본인 명의 계좌 등록이 필요해요.</div>
            </>
          )}
        </div>

        {/* 오른쪽: 등록/변경 폼 */}
        <div className="mb-card" style={{ margin: 0 }}>
          <h3 className="mb-section-title">{hasBank ? '계좌 변경' : '계좌 등록'}</h3>
          <div className="mb-form-group">
            <label className="mb-form-label">은행</label>
            <select
              className="mb-form-control"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            >
              <option value="">은행을 선택해주세요</option>
              {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            {isEtc && (
              <input
                className="mb-form-control"
                style={{ marginTop: 8 }}
                placeholder="은행명을 직접 입력해주세요"
                value={bankNameCustom}
                onChange={(e) => setBankNameCustom(e.target.value)}
              />
            )}
          </div>
          <div className="mb-form-group">
            <label className="mb-form-label">계좌번호</label>
            <input
              className="mb-form-control"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="하이픈(-) 없이 숫자만 입력해주세요"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            />
          </div>
          <div className="mb-form-group">
            <label className="mb-form-label">예금주</label>
            <input
              className="mb-form-control"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder="예금주명을 입력해주세요"
            />
            <div className="mb-info-hint">예금주명은 프리랜서 계약자명과 반드시 일치해야 해요.</div>
          </div>
          <button
            type="button"
            className="mb-btn mb-btn-primary mb-btn-full"
            onClick={onSubmit}
            disabled={sending}
          >
            {sending ? '처리 중...' : '계좌 저장하기'}
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="mbm-overlay" onClick={() => setModalOpen(false)}>
          <div className="mb-card mbm" style={{ width: 400, margin: 0, textAlign: 'center', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>{modalMsg}</p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setModalOpen(false)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluencerBankCheck;
